import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PaymentBrickCTA } from './PaymentBrickCTA';
import { OfferProductStep, type LibraryResource } from './OfferProductStep';
import { type OfferResource } from './OfferResourcesCarousel';
import { trackEvent } from '../utils/tracking';
const BACKEND_URL = 'https://mercadopago-her-backend.vercel.app';
const KLAVIYO_CHECKOUT_WEBHOOK = 'https://hook.us2.make.com/sffamlhyokha9ludn4n8dzher7nw5tdy';
const getCookie = (name: string) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : undefined;
};
const checkEmail = (value: string): boolean => {
  if (!value || value.length < 6) return false;
  const input = document.createElement('input');
  input.type = 'email';
  input.value = value;
  return input.checkValidity();
};
const checkApprovedReturn = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status') || params.get('collection_status');
    const externalReference = params.get('external_reference');
    return status === 'approved' && !!externalReference;
  } catch {
    return false;
  }
};
const isPostPurchasePreview = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('preview') === 'postpurchase';
  } catch {
    return false;
  }
};
const PRODUCT_CONTENT: OfferResource[] = [{
  title: 'Protocolo H.E.R.',
  detail: '21 días · 3 fases · acceso digital',
  image: 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/ebook_430x.webp?v=1780543244'
}, {
  title: 'Recetario H.E.R. · 63 recetas',
  detail: 'Comidas antiinflamatorias para acompañar el protocolo.',
  image: 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/sOWIBzOpm0inWncyzKYoM_430x.jpg?v=1780543245'
}, {
  title: 'H.E.R. Movement',
  detail: '21 rutinas progresivas de 15–25 minutos.',
  image: 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/S8SNVUZHdzf8t092yIlAK_430x.jpg?v=1780543245'
}, {
  title: 'Guía de compras + Meal Prep',
  detail: 'Organizá tus compras y comidas sin improvisar.',
  image: 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/1I1NNttJTHz_ql3yYujuX_430x.jpg?v=1780543245'
}, {
  title: 'Tracker H.E.R.',
  detail: 'Seguimiento simple para sostener los 21 días.',
  image: 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/v5gVLGm07qY3WP7hCr5Mp_430x.jpg?v=1780543245'
}, {
  title: 'Noche en Calma',
  detail: 'Rutina de 7 noches para cerrar el día con más calma.',
  image: 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/qushb3WzLl3BYpA9n1eL_430x.webp?v=1780543244'
}];
const LIBRARY: LibraryResource[] = [{
  title: 'El Código Hormonal',
  price: 10000,
  description: 'Entendé la fisiología detrás de los cambios y cómo ritmo, glucosa, sueño y sistema nervioso se relacionan con tu bienestar.',
  image: 'https://archivos.simpleymejor.com/httpsarchivos.simpleymejor.comcodigo-hormonal-portada.png.png'
}, {
  title: 'Cómo leer tu análisis de sangre',
  price: 8000,
  description: 'Aprendé a leer los datos que ya tenés y conocé los marcadores que conviene mirar más allá del “todo está normal”.',
  image: 'https://archivos.simpleymejor.com/httpsarchivos.simpleymejor.comanalisis-sangre-portada.png.png'
}, {
  title: 'Suplementación de Precisión',
  price: 6000,
  description: 'Conocé formas, dosis, timing y cómo leer una etiqueta para elegir suplementos con más criterio.',
  image: 'https://archivos.simpleymejor.com/httpsarchivos.simpleymejor.comsuplementacion-precision-portada.png.png'
}];
export function OfferSection() {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'product' | 'payment'>('product');
  const [submitting, setSubmitting] = useState(false);
  const [postPurchase, setPostPurchase] = useState(() => isPostPurchasePreview());
  const inputRef = useRef<HTMLInputElement>(null);
  const isValid = checkEmail(email);
  const [purchaseAmount, setPurchaseAmount] = useState(12000);
  const [purchaseType, setPurchaseType] = useState<'her' | 'her_biblioteca'>('her');
  useEffect(() => {
    if (checkApprovedReturn()) setPostPurchase(true);
  }, []);
  useEffect(() => {
    try {
      const saved = localStorage.getItem('her_email');
      if (saved && checkEmail(saved)) setEmail(saved);
    } catch {}
  }, []);
  const handleStart = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      localStorage.setItem('her_email', email);
    } catch {}
    const eventId = `HER-CHK-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    trackEvent('email_submitted', {
      section: 'offer',
      email_domain: email.split('@')[1]
    });
    fetch(`${BACKEND_URL}/api/capture-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        fbp: getCookie('_fbp'),
        fbc: getCookie('_fbc'),
        source: 'her-landing-offer'
      })
    }).catch(() => {});
    fetch(KLAVIYO_CHECKOUT_WEBHOOK, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        event_id: eventId,
        metric_name: 'HER Checkout Started',
        product: purchaseType === 'her_biblioteca' ? 'HER + Biblioteca' : 'HER',
        source: '15minutos',
        amount: purchaseAmount,
        currency: 'ARS',
        timestamp: new Date().toISOString(),
        fbp: getCookie('_fbp'),
        fbc: getCookie('_fbc')
      })
    }).catch(() => {});
    setSubmitting(false);
    setStep('payment');
  }, [email, isValid, submitting, purchaseAmount, purchaseType]);
  const handleChangeEmail = () => {
    setStep('product');
    setTimeout(() => inputRef.current?.focus(), 100);
  };
  const handleBackToSelection = () => {
    setStep('product');
  };
  const selectPurchase = (type: 'her' | 'her_biblioteca') => {
    setPurchaseType(type);
    setPurchaseAmount(type === 'her_biblioteca' ? 20000 : 12000);
    trackEvent('offer_selected', {
      section: 'offer',
      product_type: type,
      amount: type === 'her_biblioteca' ? 20000 : 12000
    });
  };
  return <section id="offer" className="py-20 md:py-28 px-4 md:px-8 relative overflow-hidden" style={{
    background: '#0D0C0A',
    paddingTop: '0px',
    paddingBottom: '0px'
  }} data-id="element-568" data-mp="22vx6j5i6wa0u">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] bg-gold/[0.03] blur-[160px] rounded-full pointer-events-none" data-id="element-569" data-mp="1tuqmwnk8uj3d" />
    <div className="max-w-md mx-auto relative z-10" data-id="element-570" data-mp="ies966e4f8vt">
      <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="card-premium !border-gold/25 px-5 py-10 md:px-8 md:py-12 shadow-gold text-center space-y-7" style={{
        paddingTop: "0px",
        paddingBottom: "0px"
      }} data-id="element-571" data-mp="idzcxzcsg1rx">
        {postPurchase ? <div className="space-y-7 text-left" data-id="element-572" data-mp="ou968jebqm7s">
          <p className="font-display text-[22px] md:text-[26px] text-[#F0E8D8] leading-snug text-center" data-id="element-573" data-mp="jvfv2xmif7as">✨ ¡Gracias por tu compra!</p>
          <p className="font-body text-[15px] text-[#F0E8D8]/75 text-center leading-relaxed" data-id="element-574" data-mp="3scix726ibyhl">Tu acceso a H.E.R. fue generado y ya está siendo enviado al email que usaste al comprar.</p>
          <div className="rounded-[4px] px-5 py-4 space-y-3" style={{
            background: 'rgba(200,168,112,0.06)',
            border: '0.5px solid rgba(200,168,112,0.18)'
          }} data-id="element-575" data-mp="5x7zou54ge1q">
            <p className="text-[12px] uppercase tracking-[0.16em] text-[#C8A870] font-semibold" style={{
              fontFamily: 'sans-serif'
            }} data-id="element-576" data-mp="39khvzt69fw50">📩 Revisá tu email</p>
            <p className="font-body text-[14px] text-[#F0E8D8]/70 leading-relaxed" data-id="element-577" data-mp="2j3df3ix2hcma">En unos minutos vas a recibir tu acceso. Si no lo encontrás, mirá también <strong className="text-[#F0E8D8]/90 font-medium" data-id="element-578" data-mp="2o2gjr294xgtq">Spam / Correo no deseado</strong> y <strong className="text-[#F0E8D8]/90 font-medium" data-id="element-579" data-mp="2t08c1xdoz4yg">Promociones</strong>.</p>
            <p className="font-body text-[14px] text-[#F0E8D8]/70 leading-relaxed" data-id="element-580" data-mp="2y0e61k7fp3jp">Para asegurarte de recibirlo, agregá <span className="text-[#C8A870] font-medium" data-id="element-581" data-mp="320ogb929eg4d">her@simpleymejor.com</span> a tus contactos.</p>
          </div>
          <button type="button" onClick={() => {
            window.location.href = 'mailto:';
          }} className="w-full py-4 font-sans font-bold uppercase tracking-[0.12em] text-[13px] rounded-[3px]" style={{
            background: '#C8A870',
            color: '#0a0807'
          }} data-id="element-582" data-mp="36z7xyx9tl5qx">Abrir mi email →</button>
        </div> : <AnimatePresence mode="wait" data-id="element-583" data-mp="3lnvrky03v5e8">
          {step === 'product' && <OfferProductStep email={email} isValid={isValid} submitting={submitting} inputRef={inputRef} resources={PRODUCT_CONTENT} library={LIBRARY} purchaseType={purchaseType} onEmailChange={setEmail} onSubmit={handleStart} onSelectPurchase={selectPurchase} data-id="element-584" data-mp="3ps3kz8hid9is" />}

          {step === 'payment' && <motion.div key="payment" initial={{
            opacity: 0,
            x: 16
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 1,
            x: -16
          }} className="space-y-6" data-id="element-585" data-mp="154bl6t4cvy9v">
            <button type="button" onClick={handleBackToSelection} className="block text-left text-[11px] uppercase tracking-[0.12em] text-[#C8A870]/65 transition-colors hover:text-[#C8A870]" data-id="element-586" data-mp="1ssuv1d320j42">
              ← Volver a la selección
            </button>

            <div className="text-center space-y-3" data-id="element-587" data-mp="1j28khxa72is8">
              <p className="uppercase tracking-[0.24em] text-[#C8A870] text-[11px] font-semibold" data-id="element-588" data-mp="2giffk0euvofg">PASO 3 · PAGO SEGURO</p>
              <p className="font-display text-[25px] text-[#F0E8D8]" data-id="element-589" data-mp="2brfj0w9nf8dh">Tu acceso está listo</p>

              <div className="rounded-[8px] border border-[#C8A870]/15 bg-[#C8A870]/[0.035] px-4 py-4" data-id="element-590" data-mp="3vgavahbt5l2a">
                <p className="font-body text-[15px] text-[#F0E8D8]/45" data-id="element-591" data-mp="3qij2zm793wxk">Email de acceso</p>
                <p className="font-body text-[14px] text-[#F0E8D8]/80 mt-1 truncate" data-id="element-592" data-mp="9a5kbwxf5yqx">{email}</p>
                <button type="button" onClick={handleChangeEmail} className="text-[12px] uppercase tracking-[0.1em] text-[#C8A870] mt-2" data-id="element-593" data-mp="4at0ll1uh7sk">Cambiar email</button>
              </div>

              <p className="font-body text-[14px] text-[#F0E8D8]/40 leading-relaxed" data-id="element-594" data-mp="i7y3jc69hqjl">Tu acceso se enviará a este email. Si no lo encontrás, revisá <span className="text-[#C8A870]/80" data-id="element-595" data-mp="s0dh7xaizsyo">Spam o Promociones</span>.</p>

              <div data-id="element-596" data-mp="n1apfejk3j8n">
                <p className="font-body text-[8px] uppercase tracking-[0.2em] text-[#F0E8D8]/30" data-id="element-597" data-mp="n0r5jsacikrz">Total a pagar</p>
                <p className="font-display text-[48px] text-gradient-gold leading-none mt-1" data-id="element-598" data-mp="111lgahkv8xtt">${purchaseAmount.toLocaleString('es-AR')}</p>
                <p className="font-display mt-4 text-[18px] font-normal leading-tight text-gradient-gold" data-id="element-599" data-mp="wxdogbj431rd">{purchaseType === 'her_biblioteca' ? 'H.E.R. + Biblioteca completa' : 'Protocolo H.E.R. · 21 días'}</p>
              </div>
            </div>

            <div className="pt-1" data-id="element-600" data-mp="16nlxhcioyohi">
              <p className="font-display text-[20px] text-[#F0E8D8] text-left" style={{
                fontSize: "16px"
              }} data-id="element-601" data-mp="16mjahfohp66v">Elegí cómo pagar</p>
              <div className="mt-3" data-id="element-602" data-mp="wwrag5xmfkv0">
                <PaymentBrickCTA email={email} amount={purchaseAmount} productType={purchaseType} title={purchaseType === 'her_biblioteca' ? 'H.E.R. + Biblioteca Completa' : 'Protocolo H.E.R. — Acceso Completo'} onApproved={() => setPostPurchase(true)} data-id="element-603" data-mp="wvol42fy5qh7" />
              </div>
            </div>
          </motion.div>}
        </AnimatePresence>}

        {!postPurchase && <div className="mt-1 rounded-[12px] border border-[#C8A870]/20 bg-[#0b0907]/65 px-4 py-5" data-id="element-604" data-mp="dxvhexxi1cir">
          <p className="uppercase tracking-[0.2em] text-[#C8A870] font-semibold text-[11px]" data-id="element-605" data-mp="ixhv18h8ous3">✦ Compra protegida</p>
          <p className="font-display text-[19px] text-[#F0E8D8] mt-2" data-id="element-606" data-mp="2knfk95dm7mgi">30 días para probar H.E.R.</p>
          <p className="font-body text-[12px] text-[#F0E8D8]/55 mt-1" data-id="element-607" data-mp="2fuvy3k0v3j03">Si no es para vos, te devolvemos el 100% de tu dinero.</p>
          <img src="https://cdn.magicpatterns.com/uploads/1a9ZqWHLFGeJEBrZWczanr/mp.webp" alt="Pago seguro con Mercado Pago y medios de pago disponibles" loading="lazy" decoding="async" className="mt-4 mx-auto max-w-[220px] w-full h-auto object-contain opacity-90" data-id="element-608" data-mp="2buc58snbn1jr" />
        </div>}
      </motion.div>
    </div>
  </section>;
}